import { useState, useEffect, useCallback, useRef } from 'react';
import posthog from 'posthog-js';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';import axios from 'axios';
import {
  Upload, FileText, Trash2, Download, Sparkles,
  CheckCircle2, Clock, File as FileIcon, Eye, Cpu, Share2, Copy as CopyIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { value: 'resume', label: 'Resume / CV', model: 'Claude' },
  { value: 'id', label: 'ID Card', model: 'Gemini' },
  { value: 'passport', label: 'Passport', model: 'Gemini' },
  { value: 'drivers_license', label: "Driver's License", model: 'Gemini' },
  { value: 'bank_statement', label: 'Bank Statement', model: 'Claude' },
  { value: 'payslip', label: 'Payslip', model: 'Claude' },
  { value: 'tax_record', label: 'Tax Record', model: 'Claude' },
  { value: 'certification', label: 'Certification', model: 'Claude' },
  { value: 'other', label: 'Other', model: 'Auto' },
];

const ACCEPT = '.pdf,.doc,.docx,.png,.jpg,.jpeg,.webp';
const MAX_SIZE = 10 * 1024 * 1024;

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [parsing, setParsing] = useState({});
  const [selectedType, setSelectedType] = useState('resume');
  const [dragOver, setDragOver] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [creatingShare, setCreatingShare] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/documents`, { withCredentials: true });
      setDocuments(data);
    } catch (e) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_SIZE) {
      toast.error('File too large (max 10MB)');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/documents/upload?doc_type=${selectedType}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      toast.success(`Uploaded ${file.name}`);
      fetchDocuments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/documents/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchDocuments();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleParse = async (id) => {
    setParsing((p) => ({ ...p, [id]: true }));
    try {
      const { data } = await axios.post(`${API}/documents/${id}/parse`, {}, { withCredentials: true });
      toast.success(`Parsed — ${data.fields_extracted || 0} fields extracted via ${data.model_used || 'AI'}`);
      try { posthog.capture('document_parsed', { model_used: data.model_used, fields_extracted: data.fields_extracted }); } catch (e) {}
      fetchDocuments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse document');
    } finally {
      setParsing((p) => ({ ...p, [id]: false }));
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/documents/${doc.document_id}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download');
    }
  };

  const handleShareAll = async () => {
    const parsedDocs = documents.filter((d) => d.is_parsed);
    if (parsedDocs.length === 0) {
      toast.error('Parse at least one document first');
      return;
    }
    setCreatingShare(true);
    try {
      const { data } = await axios.post(`${API}/vault/share`, {
        document_ids: parsedDocs.map((d) => d.document_id),
        include_profile: true,
        expires_in_days: 7,
        label: 'Application Pack',
      }, { withCredentials: true });
      setShareUrl(window.location.origin + data.url);
      try { posthog.capture('vault_shared', { doc_count: parsedDocs.length }); } catch (e) {}
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create share');
    } finally {
      setCreatingShare(false);
    }
  };

  const formatSize = (b) => {
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
    return (b / 1024 / 1024).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="documents-loading">
        <p className="text-sm uppercase tracking-widest text-muted-foreground">LOADING DOCUMENTS...</p>
      </div>
    );
  }

  const parsedCount = documents.filter((d) => d.is_parsed).length;
  const currentType = DOC_TYPES.find((t) => t.value === selectedType);

  return (
    <div className="container mx-auto px-4 md:px-8 py-8 max-w-5xl" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl mb-1">DOCUMENTS</h1>
          <p className="text-muted-foreground text-sm">
            Drop a file. AI extracts the data. Your profile updates itself.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {parsedCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleShareAll}
              disabled={creatingShare}
              className="uppercase text-xs tracking-wider"
              data-testid="share-vault-btn"
            >
              <Share2 className="w-3.5 h-3.5 mr-2" />
              {creatingShare ? 'CREATING...' : 'SHARE PACK'}
            </Button>
          )}
          <div className="grid grid-cols-2 gap-px bg-border border border-border">
            <div className="bg-background px-5 py-2.5 text-center">
              <div className="font-heading text-xl leading-none">{documents.length}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">TOTAL</div>
            </div>
            <div className="bg-background px-5 py-2.5 text-center">
              <div className="font-heading text-xl leading-none text-accent">{parsedCount}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">PARSED</div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Zone */}
      <div className="mb-8" data-testid="upload-zone">
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-end mb-3">
          <div className="flex-1 w-full">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 block">
              DOCUMENT TYPE
            </Label>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-full md:w-72" data-testid="doc-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value} data-testid={`doc-type-option-${t.value}`}>
                    <span className="flex items-center gap-3">
                      <span>{t.label}</span>
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        · {t.model}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <Cpu className="w-3 h-3" />
            Will parse with <span className="text-primary font-mono">{currentType?.model}</span>
          </div>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed p-10 md:p-14 text-center transition-all ${
            dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground'
          } ${uploading ? 'opacity-60 pointer-events-none' : 'cursor-pointer'}`}
          onClick={() => fileInputRef.current?.click()}
          data-testid="drop-zone"
        >
          <Input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={ACCEPT}
            onChange={handleFileInput}
            data-testid="file-input"
          />
          <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="font-heading text-sm uppercase tracking-wider mb-1">
            {uploading ? `UPLOADING... ${uploadProgress}%` : 'DROP FILE OR CLICK TO UPLOAD'}
          </div>
          <div className="text-xs text-muted-foreground">
            PDF, DOC, DOCX, PNG, JPG · Max 10 MB
          </div>
          {uploading && (
            <div className="mt-4 max-w-xs mx-auto h-1 bg-secondary">
              <div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
          )}
        </div>
      </div>

      {/* List */}
      {documents.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center" data-testid="no-documents">
          <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-heading uppercase text-sm tracking-wider mb-1">NO DOCUMENTS YET</h3>
          <p className="text-xs text-muted-foreground">Upload your first document to start building your profile.</p>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="documents-list">
          {documents.map((doc) => (
            <DocCard
              key={doc.document_id}
              doc={doc}
              parsing={parsing[doc.document_id]}
              onDelete={() => handleDelete(doc.document_id)}
              onParse={() => handleParse(doc.document_id)}
              onDownload={() => handleDownload(doc)}
              onView={() => setViewDoc(doc)}
              formatSize={formatSize}
            />
          ))}
        </div>
      )}

      {/* Parsed Data Viewer */}
      <Dialog open={!!viewDoc} onOpenChange={() => setViewDoc(null)}>        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wider">
              EXTRACTED DATA
            </DialogTitle>
          </DialogHeader>
          {viewDoc && (
            <div className="space-y-4" data-testid="parsed-data-viewer">
              <div className="flex items-center justify-between text-xs uppercase tracking-widest text-muted-foreground">
                <span>{viewDoc.original_filename}</span>
                {viewDoc.model_used && (
                  <span className="font-mono text-primary">VIA {viewDoc.model_used.toUpperCase()}</span>
                )}
              </div>
              <pre
                className="bg-secondary/40 border border-border p-4 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-words"
                data-testid="parsed-data-json"
              >
                {JSON.stringify(viewDoc.parsed_data || {}, null, 2)}
              </pre>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocCard({ doc, parsing, onDelete, onParse, onDownload, onView, formatSize }) {
  const typeLabel = DOC_TYPES.find((t) => t.value === doc.doc_type)?.label || doc.doc_type;
  const fieldCount = Object.values(doc.parsed_data || {}).filter(
    (v) => v !== '' && v !== null && (Array.isArray(v) ? v.length : true)
  ).length;

  return (
    <div className="border border-border bg-background p-4 hover:bg-secondary/20 transition-colors" data-testid={`doc-${doc.document_id}`}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 border border-border flex items-center justify-center flex-shrink-0">
          <FileIcon className="w-5 h-5 text-muted-foreground" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-mono text-sm truncate">{doc.original_filename}</h3>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                <span className="text-foreground">{typeLabel}</span>
                <span>·</span>
                <span>{formatSize(doc.size)}</span>
                <span>·</span>
                <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                {doc.is_parsed && doc.model_used && (
                  <>
                    <span>·</span>
                    <span className="text-primary font-mono">{doc.model_used}</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {doc.is_parsed ? (
                <Button size="sm" variant="ghost" onClick={onView} className="text-xs uppercase tracking-wider" data-testid={`view-btn-${doc.document_id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1" /> {fieldCount} fields
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={onParse}
                  disabled={parsing}
                  className="brutalist-shadow-sm uppercase text-xs tracking-wider"
                  data-testid={`parse-btn-${doc.document_id}`}
                >
                  {parsing ? (
                    <><Clock className="w-3 h-3 mr-1 animate-spin" /> PARSING</>
                  ) : (
                    <><Sparkles className="w-3 h-3 mr-1" /> PARSE</>
                  )}
                </Button>
              )}
              <Button size="icon" variant="ghost" onClick={onDownload} data-testid={`download-btn-${doc.document_id}`}>
                <Download className="w-4 h-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={onDelete} className="text-destructive hover:text-destructive" data-testid={`delete-btn-${doc.document_id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {doc.is_parsed && (
            <div className="mt-3 flex items-center gap-2 text-xs">
              <CheckCircle2 className="w-3.5 h-3.5 text-accent flex-shrink-0" />
              <span className="text-muted-foreground truncate">
                {Object.keys(doc.parsed_data || {}).slice(0, 6).map((k) => k.replace(/_/g, ' ')).join(' · ')}
                {Object.keys(doc.parsed_data || {}).length > 6 && ` · +${Object.keys(doc.parsed_data).length - 6}`}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
